import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { pluck } from 'rxjs/operators';
import { ProjectService } from 'src/app/core/services/project.service';
import { LoadingSpaceType } from 'src/app/core/types';
import { Project } from '../../../../core/objects/project';
import { maxErrorKey } from '../../../../shared/validators/validators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'gl-loading-space-door-size',
    templateUrl: './loading-space-door-size.component.html',
    styleUrls: ['./loading-space-door-size.component.scss'],
    standalone: false
})
export class LoadingSpaceDoorSizeComponent implements OnInit, OnChanges, OnDestroy {
  @Output() public submitEvent = new EventEmitter();
  @Input() public loadingSpace: LoadingSpaceType;
  @Input() public chosenLoadingSpace: LoadingSpaceType;
  @Input() public isEdit = false;
  @ViewChild('f', { static: false }) public formEl: FormGroupDirective;
  @Input() public project: Project;
  public form = this.generateForm();
  public unit: string;
  public loadingSpaceMod: LoadingSpaceType;
  public isSet = false;
  public isTrailerOpen = true;
  public isSemiTrailerOpen = false;
  private pattern = '^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$';
  private subscription: Subscription;

  constructor(private projectService: ProjectService) {}

  public openTab(tabName: string): void {
    this.isTrailerOpen = false;
    this.isSemiTrailerOpen = false;
    switch (tabName) {
      case 'trailer':
        this.isTrailerOpen = true;
        break;
      case 'semitrailer':
        this.isSemiTrailerOpen = true;
        break;
    }
  }

  public ngOnInit() {
    this.isSet = this.loadingSpace && this.loadingSpace.set;
    this.updateSpaceMod();
    this.subscription = this.form.valueChanges.subscribe(() => {
      this.addMaxValidators();
      this.updateSpaceMod();
      this.submit();
    });
    if (this.isEdit) {
      this.form.enable();
    } else {
      this.form.disable();
    }
    this.projectService.project.pipe(pluck('options')).subscribe((options) => {
      this.unit = options?.unit;
    });
    this.onChosen(this.loadingSpace);
  }

  private setControlValidator(name: string, max: number): void {
    this.form.controls[name].clearValidators();
    this.form
      .get(name)
      .setValidators([
        Validators.min(0),
        maxErrorKey(max, 'doorMax'),
        Validators.pattern(this.pattern),
        Validators.required,
      ]);
    this.form.controls[name].updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private addMaxValidators(): void {
    if (!this.loadingSpace) {
      return;
    }
    if (this.loadingSpace.width && this.loadingSpace.height && this.loadingSpace.length) {
      const maxWidth = this.loadingSpace.width;
      const maxHeight = this.loadingSpace.height;
      this.setControlValidator('trailerDoorHeight', maxHeight);
      this.setControlValidator('trailerDoorWidth', maxWidth);
    }
    if (
      this.loadingSpace.set &&
      this.loadingSpace.width1 &&
      this.loadingSpace.height1 &&
      this.loadingSpace.length1
    ) {
      const maxWidth2 = this.loadingSpace.width1;
      const maxHeight2 = this.loadingSpace.height1;
      this.setControlValidator('semiTrailerDoorHeight', maxHeight2);
      this.setControlValidator('semiTrailerDoorWidth', maxWidth2);
    }
  }

  private updateSpaceMod(): void {
    const trailerDoorHeightValue = this.form.get('trailerDoorHeight').value as number;
    const trailerDoorWidthValue = this.form.get('trailerDoorWidth').value as number;
    const semiTrailerDoorHeightValue = this.form.get('semiTrailerDoorHeight').value as number;
    const semiTrailerDoorWidthValue = this.form.get('semiTrailerDoorWidth').value as number;
    this.loadingSpaceMod = {
      name: this.loadingSpace.name,
      length: this.loadingSpace.length,
      width: this.loadingSpace.width,
      height: this.loadingSpace.height,
      limit: this.loadingSpace.limit,
      length1: this.loadingSpace.length1,
      width1: this.loadingSpace.width1,
      height1: this.loadingSpace.height1,
      limit1: this.loadingSpace.limit1,
      set: true,
      type: this.loadingSpace.type,
      id: null,
      unit: this.loadingSpace.unit,
      axis: [],
      useInRecommendation: null,
      wheelTrailerWidth: null,
      wheelTrailerHeight: null,
      wheelTrailerLength: null,
      wheelTrailerDistance: null,
      wheelSemiTrailerWidth: null,
      wheelSemiTrailerHeight: null,
      wheelSemiTrailerLength: null,
      wheelSemiTrailerDistance: null,
      coolerTrailerWidth: null,
      coolerTrailerHeight: null,
      coolerTrailerLength: null,
      coolerTrailerFloorHeight: null,
      coolerSemiTrailerWidth: null,
      coolerSemiTrailerHeight: null,
      coolerSemiTrailerLength: null,
      coolerSemiTrailerFloorHeight: null,
      trailerDoorHeight: trailerDoorHeightValue,
      trailerDoorWidth: trailerDoorWidthValue,
      semiTrailerDoorHeight: semiTrailerDoorHeightValue,
      semiTrailerDoorWidth: semiTrailerDoorWidthValue,
      varioTrailerFloorHeight: null,
      varioSemiTrailerFloorHeight: null,
    };
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.isEdit && changes.isEdit.currentValue) {
      this.isEdit = true;
      this.form.enable();
    } else if (changes.isEdit && !changes.isEdit.currentValue) {
      this.isEdit = false;
      this.form.disable();
    }
    this.addMaxValidators();
    this.isSet = !!changes.loadingSpace && !!changes.loadingSpace.currentValue.set;
    if (
      changes.chosenLoadingSpace &&
      changes.chosenLoadingSpace.currentValue !== changes.chosenLoadingSpace.previousValue
    ) {
      this.onChosen(changes.chosenLoadingSpace.currentValue as LoadingSpaceType);
    }
  }

  public onChosen(loadingSpace: LoadingSpaceType): void {
    if (!loadingSpace) {
      return;
    }
    if (loadingSpace.trailerDoorHeight && loadingSpace.trailerDoorWidth) {
      this.form.get('trailerDoorHeight').patchValue(loadingSpace.trailerDoorHeight);
      this.form.get('trailerDoorWidth').patchValue(loadingSpace.trailerDoorWidth);
    } else {
      this.form.get('trailerDoorHeight').patchValue(null);
      this.form.get('trailerDoorWidth').patchValue(null);
    }
    if (loadingSpace.semiTrailerDoorHeight && loadingSpace.semiTrailerDoorWidth) {
      this.form.get('semiTrailerDoorHeight').patchValue(loadingSpace.semiTrailerDoorHeight);
      this.form.get('semiTrailerDoorWidth').patchValue(loadingSpace.semiTrailerDoorWidth);
    } else {
      this.form.get('semiTrailerDoorHeight').patchValue(null);
      this.form.get('semiTrailerDoorWidth').patchValue(null);
    }
  }

  public submit() {
    if (this.form.valid) {
      this.submitEvent.emit(this.form.value);
    }
  }

  public delete() {
    this.form.reset();
    this.submitEvent.emit({
      trailerDoorHeight: null,
      trailerDoorWidth: null,
      semiTrailerDoorHeight: null,
      semiTrailerDoorWidth: null,
    });
  }

  private generateForm() {
    return new UntypedFormGroup({
      trailerDoorHeight: new UntypedFormControl(null),
      trailerDoorWidth: new UntypedFormControl(null),
      semiTrailerDoorHeight: new UntypedFormControl(null),
      semiTrailerDoorWidth: new UntypedFormControl(null),
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
